// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/TouristTaxWidget.tsx
// 🔧 PURPOSE: "Imposta di soggiorno" card in MyStay. Amber while the host has
//             requested it and it is unpaid (with the in-app pay button),
//             green once the channel manager webhook marks it paid.
//             Hidden entirely when the booking has no request (parent decides).
//             Aspetto del mockup "MsPrimaArrivo": la fascia ambra → arancio
//             con l'icona nel vetro, l'importo in Fraunces, "PAGA ORA IN APP"
//             indaco → viola; pagata è la riga verde chiusa sotto le ricevute.
// ============================================

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Landmark, CreditCard, KeyRound, Lock, CheckCircle2, Receipt, ChevronDown, Loader2 } from 'lucide-react';
import type { TouristTaxInfo } from '../lib/guestApi';
import { cn } from '../lib/utils';
import {
  BandCard, CREAM_BOX, FOCUS_RING, INK_TEXT, MUTED_TEXT, RuleNote, SERVICE_BUTTON, STAY_BAND, STAY_BTN,
} from './stay/ui';

interface TouristTaxWidgetProps {
  touristTax: TouristTaxInfo;
  onPay: () => void;
  /** Stripe said "complete" but the server hasn't confirmed yet: no pay button. */
  processing?: boolean;
}

export const formatEuro = (amount: number, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(amount);
  } catch {
    return `€${amount.toFixed(2)}`;
  }
};

const TouristTaxWidget: React.FC<TouristTaxWidgetProps> = ({ touristTax, onPay, processing = false }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const amount = formatEuro(touristTax.amount, locale);
  const isPaid = touristTax.status === 'paid';
  // Pagata: una riga verde chiusa; i dettagli si aprono al tocco.
  const [paidOpen, setPaidOpen] = useState(false);

  // Prenotazione di gruppo: l'importo grande è il TOTALE di tutti gli
  // appartamenti, non la quota di uno solo. Senza l'elenco qui sotto l'ospite
  // vedrebbe una cifra che non corrisponde a nessuna delle sue prenotazioni e
  // non capirebbe di star pagando anche per gli altri.
  const group = touristTax.group;
  const apartmentCount = group?.apartments.length || 0;
  const isGroup = Boolean(group) && apartmentCount > 1;

  const groupList = isGroup && group ? (
    <ul className={cn(CREAM_BOX, 'm-0 list-none divide-y divide-[#E7DCCB] px-3.5 py-1 dark:divide-stone-700')}>
      {group.apartments.map((apt, idx) => (
        <li key={`${apt.apartmentName}-${idx}`} className="flex items-center justify-between gap-2.5 py-2.5 text-[14px]">
          <span className={cn('flex min-w-0 items-center gap-1.5', INK_TEXT)}>
            {apt.status === 'paid' && (
              <CheckCircle2 aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-emerald-700 dark:text-emerald-300" />
            )}
            <span className="truncate">{apt.apartmentName}</span>
          </span>
          <span
            className={
              apt.status === 'paid'
                ? cn('tabular-nums line-through', MUTED_TEXT)
                : cn('font-bold tabular-nums', INK_TEXT)
            }
          >
            {formatEuro(apt.amount, locale)}
          </span>
        </li>
      ))}
    </ul>
  ) : null;

  if (isPaid) {
    const paidDate = touristTax.paidAt
      ? new Date(touristTax.paidAt).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
      : null;

    // Pagata non chiede più niente: resta una riga compatta ("✓ Tassa di
    // soggiorno · Pagata · € X") che al tocco apre data, quietanza,
    // appartamenti e nota.
    return (
      <section
        data-testid="tourist-tax-widget-paid"
        className="overflow-hidden rounded-[20px] border border-[#BFE3CF] bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30"
      >
        <button
          type="button"
          onClick={() => setPaidOpen((o) => !o)}
          aria-expanded={paidOpen}
          aria-controls="tourist-tax-paid-details"
          className={cn('flex min-h-[56px] w-full items-center gap-2.5 px-3.5 py-2 text-left', FOCUS_RING)}
        >
          <CheckCircle2 aria-hidden="true" className="h-5 w-5 flex-shrink-0 text-emerald-700 dark:text-emerald-300" strokeWidth={2.1} />
          <span className="flex min-w-0 flex-1 flex-col gap-px">
            <span className="text-[15px] font-bold leading-snug text-emerald-900 dark:text-emerald-100">{t('myStay.touristTax.rowTitle')}</span>
            <span className="text-[13px] font-bold leading-snug text-emerald-800 dark:text-emerald-200">
              {t('myStay.touristTax.rowPaid')} · <span className="whitespace-nowrap tabular-nums">{amount}</span>
            </span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn('h-[18px] w-[18px] flex-shrink-0 text-emerald-700 transition-transform duration-200 dark:text-emerald-300', paidOpen && 'rotate-180')}
            strokeWidth={2.4}
          />
        </button>
        <AnimatePresence initial={false}>
          {paidOpen && (
            <motion.div
              id="tourist-tax-paid-details"
              key="details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <div className="flex flex-col gap-2.5 px-3.5 pb-3.5 text-emerald-900 dark:text-emerald-100">
                {paidDate && <p className="m-0 text-[13.5px] leading-snug">{t('myStay.touristTax.paidOn', { date: paidDate })}</p>}
                {isGroup && (
                  <p className="m-0 text-[13px] leading-snug">{t('myStay.touristTax.forApartments', { count: apartmentCount })}</p>
                )}
                {groupList}
                <p className="m-0 flex items-start gap-2 rounded-xl border border-[#BFE3CF] bg-nva-card px-3 py-2.5 text-[13.5px] leading-[1.4] text-emerald-800 dark:border-emerald-900/60 dark:bg-stone-900 dark:text-emerald-200">
                  <Receipt aria-hidden="true" className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.1} />
                  <span>
                    {touristTax.receiptNumber
                      ? `${t('myStay.touristTax.receipt', { number: touristTax.receiptNumber })} · ${t('myStay.touristTax.receiptByEmail')}`
                      : t('myStay.touristTax.receiptByEmail')}
                  </span>
                </p>
                <p className="m-0 text-[13px] leading-snug text-emerald-800 dark:text-emerald-200">{t('myStay.touristTax.paidNote')}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    );
  }

  return (
    <BandCard
      as="section"
      data-testid="tourist-tax-widget-due"
      tint="amber"
      shadow="shadow-[0_2px_12px_rgba(194,65,12,.07)]"
      gradient={STAY_BAND.amber}
      icon={<Landmark />}
      title={t('myStay.touristTax.dueTitle')}
      titleAs="h2"
      body={(
        <>
          <div className="flex items-end justify-between gap-3">
            <span className={cn('font-display text-[34px] font-bold leading-none tabular-nums', INK_TEXT)}>{amount}</span>
            <span className={cn('max-w-[55%] text-right text-[12.5px] leading-[1.4]', MUTED_TEXT)}>
              {isGroup ? t('myStay.touristTax.forApartments', { count: apartmentCount }) : t('myStay.touristTax.subtitle')}
            </span>
          </div>

          {groupList}
          {isGroup && group && group.paidCount > 0 && (
            <p className={cn('m-0 text-[13px] leading-[1.4]', MUTED_TEXT)}>{t('myStay.touristTax.groupResidual')}</p>
          )}
          {isGroup && <p className={cn('m-0 text-[13px] leading-[1.4]', MUTED_TEXT)}>{t('myStay.touristTax.groupSinglePayment')}</p>}

          {processing ? (
            <div
              role="status"
              className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-center text-[14px] font-bold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
            >
              <Loader2 aria-hidden="true" className="h-4 w-4 flex-shrink-0 animate-spin" strokeWidth={2.4} />
              {t('myStay.touristTax.sheet.completed')}
            </div>
          ) : touristTax.canPayInApp && (
            <button type="button" onClick={onPay} className={cn(SERVICE_BUTTON, STAY_BTN.indigo, 'w-full')}>
              <CreditCard aria-hidden="true" className="h-[17px] w-[17px] flex-shrink-0" strokeWidth={2.2} />
              {t('myStay.touristTax.payNow')}
            </button>
          )}

          <RuleNote tone="amber" icon={<KeyRound />}>{t('myStay.touristTax.codeAfterPayment')}</RuleNote>

          <p className={cn('m-0 flex items-start gap-1.5 text-[12px] leading-[1.4]', MUTED_TEXT)}>
            <Lock aria-hidden="true" className="mt-px h-[13px] w-[13px] flex-shrink-0" strokeWidth={2.2} />
            {t('myStay.touristTax.secure')}
          </p>
        </>
      )}
    />
  );
};

export default TouristTaxWidget;
